import {
	BBBackendQueryOptions,
	type BidBattle,
} from "@bidbattles/bloks/lib/rapace";
import { getBaseURLs } from "@bidbattles/bloks/lib/rapace/utils";
import { Result } from "@bidbattles/bloks/lib/result";
import { useSuspenseQuery } from "@tanstack/react-query";
import { pick } from "es-toolkit";
import { produce } from "immer";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { z } from "zod/v4";
import { exponentialBackoff } from "../utils";
import { getAuthTokenFromCookie__DEV } from "./auth";

const RECONNECT_TIMER_BASE = 500;
const RECONNECT_TIMER_MAX = 5000;

abstract class BaseStore<State> {
	protected listeners: Set<() => void> = new Set();

	constructor(protected state: State) {}

	protected trigger = () => {
		for (const fn of this.listeners) {
			setTimeout(fn);
		}
	};

	getSnapshot = () => this.state;

	subscribe = (fn: () => void) => {
		this.listeners.add(fn);
		return () => {
			this.listeners.delete(fn);
		};
	};
}

class BBBattleTimerStore extends BaseStore<string> {
	private abort: AbortController | null = null;
	private reconnectTimer: Timer | undefined = undefined;
	private reconnectAttempt = 0;

	async connect() {
		if (this.abort) {
			this.abort.abort();
		}

		const abort = new AbortController();
		this.abort = abort;

		const token = getAuthTokenFromCookie__DEV();

		const result = await Result.tryAsync(() =>
			fetch(
				`${getBaseURLs().rtHttpURL}/v1/live/battles/${this.battleId}/timer`,
				{
					signal: abort.signal,
					headers: {
						"content-type": "text/event-stream",
						...(!!token && { authorization: `Bearer ${token}` }),
					},
					credentials: "include",
				},
			),
		);

		if (!result.ok) {
			this.reconnectAfterBackoff();
			return result;
		}

		const response = result.data;
		if (!response.ok) {
			this.reconnectAfterBackoff();
			return Result.error(new Error(`Status: ${response.statusText}`));
		}

		const stream = response.body?.pipeThrough(new TextDecoderStream());
		const reader = stream?.getReader();
		if (!reader) {
			this.reconnectAfterBackoff();
			return Result.error(new Error("body missing"));
		}

		abort.signal.addEventListener("abort", async () => {
			await reader.cancel("aborted");
		});

		this.reconnectAttempt = 0;
		clearTimeout(this.reconnectTimer);

		this.readerLoop(reader);
		return Result.ok(null);
	}

	private readerLoop = async (reader: ReadableStreamDefaultReader<string>) => {
		const result = await Result.tryAsync(() => reader.read());
		if (!result.ok) {
			// TODO: How exactly does connection loss affect this *after* we're already streaming?
			this.reconnectAfterBackoff();
			return;
		}

		const { value, done } = result.data;
		if (done) {
			// While we're active, the stream may "stop" with no error if no updates
			// are received for long enough (e.g. paused battle), as the server terminates
			// the connection, so we try and reconnect.
			this.reconnectAfterBackoff();
			return;
		}

		if (value) {
			const lines = value.split("\n").filter((line) => line.trim().length > 0);
			this.state = lines[lines.length - 1].replace(/^data:\s*/, "");
			this.trigger();
		}

		this.readerLoop(reader);
	};

	private reconnectAfterBackoff() {
		this.reconnectAttempt += 1;
		const waitMs = exponentialBackoff(
			RECONNECT_TIMER_BASE,
			this.reconnectAttempt,
			RECONNECT_TIMER_MAX,
		);
		console.warn(`[timer] error, waiting for ${waitMs}ms before reconnecting`);

		clearTimeout(this.reconnectTimer);
		this.reconnectTimer = setTimeout(() => {
			this.abort?.abort();
			this.abort = null;
			this.connect();
		}, waitMs);
	}

	constructor(private battleId: string) {
		super("00:00:00");
	}

	stop() {
		this.abort?.abort();
		this.reconnectAttempt = 0;
		clearTimeout(this.reconnectTimer);
		this.state = "00:00:00";

		this.trigger();
	}
}

interface Store<State> {
	subscribe(fn: () => void): () => void;
	getSnapshot(): State;
	connect(): void;
	stop(): void;
}

function useStore<TState, TStore extends Store<TState>>(
	createStore: () => TStore,
	getServerSnapshot: () => TState,
) {
	const store = useMemo(() => createStore(), [createStore]);

	useEffect(() => {
		store.connect();
		return () => store.stop();
	}, [store]);

	return [
		store,
		useSyncExternalStore(store.subscribe, store.getSnapshot, getServerSnapshot),
	] as const;
}

export function useBattleTimer(id: string) {
	const [, state] = useStore(
		useCallback(() => new BBBattleTimerStore(id), [id]),
		() => "00:00:00",
	);
	return state;
}

const wsEventSchema = z.union([
	z.object({ type: z.literal("pong") }),
	z.object({
		type: z.literal("balance_update"),
		name: z.string(),
		amount: z.number(),
		balance: z.number(),
		total_bids: z.number(),
	}),
	z.object({
		type: z.literal("bid"),
		name: z.string(),
		amount: z.number(),
		timestamp: z.coerce.date(),
	}),
	z.intersection(
		z.object({ type: z.literal("probation") }),
		z.union([
			z.object({ event: z.literal("timer"), remaining: z.string() }),
			z.object({ event: z.literal("cleared") }),
		]),
	),
	z.intersection(
		z.object({ type: z.literal("system") }),
		z.union([
			z.object({
				event: z.literal("rejected"),
				reason: z.literal("evicted"),
				message: z.string(),
			}),
			z.object({
				event: z.literal(["completion", "eviction", "cancellation"]),
				message: z.string(),
			}),
		]),
	),
]);
type WSEvent = z.infer<typeof wsEventSchema>;

type SharedBattleState = {
	me: {
		bids: Map<number, { balance: number; total_bids: number }>;
	};
	bids: Array<{ name: string; amount: number; timestamp: Date }>;
};
type BattleState = SharedBattleState &
	(
		| {
				state: "connecting";
		  }
		| { state: "connected" }
		| { state: "disconnected" }
		| { state: "evicted" }
		| { state: "completed" }
		| { state: "cancelled" }
		| { state: "probation"; remaining: string }
	);

const defaultBattleStoreState: BattleState = {
	state: "connecting",
	me: {
		bids: new Map(),
	},
	bids: [],
};

class BBBattleStore extends BaseStore<BattleState> {
	private ws: WebSocket | null = null;
	private pingTimer: Timer | undefined = undefined;
	private reconnectTimer: Timer | undefined = undefined;
	private reconnectAttempt = 0;
	private closedByServer = false;

	constructor(private battleId: string) {
		super(defaultBattleStoreState);
	}

	connect() {
		if (this.ws) {
			this.ws.close();
		}

		this.ws = new WebSocket(
			`${getBaseURLs().rtURL}/v1/live/battles/${this.battleId}`,
		);
		this.ws.addEventListener("open", () => {
			clearTimeout(this.reconnectTimer);
			this.reconnectAttempt = 0;
			this.pingTimer = setInterval(
				() => this.ws?.send(JSON.stringify({ type: "ping" })),
				5000,
			);
		});

		this.ws.addEventListener("error", (event) => {
			this.reconnectAfterBackoff(event);
		});

		this.ws.addEventListener("close", (event) => {
			if (!event.wasClean && !this.closedByServer)
				this.reconnectAfterBackoff(event);
		});

		this.ws.addEventListener("message", (event) => {
			const parsed = JSON.parse(event.data);
			const arrData = Array.isArray(parsed) ? parsed : [parsed];
			for (const maybeEvent of arrData) {
				const eventResult = wsEventSchema.safeParse(maybeEvent);
				if (!eventResult.success) {
					console.warn("[ws] received unknown event", maybeEvent);
					continue;
				}
				const event = eventResult.data;

				this.handleEvent(event);
			}
		});
	}

	private reconnectAfterBackoff(event: Event) {
		this.reconnectAttempt += 1;
		const waitMs = exponentialBackoff(
			RECONNECT_TIMER_BASE,
			this.reconnectAttempt,
			RECONNECT_TIMER_MAX,
		);
		console.warn(
			`[ws] error or closed abruptly, waiting for ${waitMs}ms before reconnecting`,
			event,
		);

		clearTimeout(this.reconnectTimer);
		clearInterval(this.pingTimer);
		this.reconnectTimer = setTimeout(() => {
			this.ws = null;
			this.connect();
		}, waitMs);
	}

	private handleEvent(event: WSEvent) {
		if (event.type !== "pong" && this.state.state === "connecting") {
			this.state = { ...this.state, state: "connected" };
			this.trigger();
		}

		switch (event.type) {
			case "pong": {
				break;
			}
			case "balance_update": {
				this.state = produce(this.state, (draft) => {
					draft.me.bids.set(event.amount, {
						balance: event.balance,
						total_bids: event.total_bids,
					});
				});
				this.trigger();
				break;
			}
			case "bid": {
				this.state = produce(this.state, (draft) => {
					draft.bids.unshift({
						name: event.name,
						amount: event.amount,
						timestamp: event.timestamp,
					});
				});
				this.trigger();
				break;
			}
			case "probation": {
				const pbEvent = event.event;
				switch (pbEvent) {
					case "timer": {
						this.state = {
							...this.state,
							state: "probation",
							remaining: event.remaining,
						};
						this.trigger();
						break;
					}
					case "cleared": {
						this.state = produce(this.state, (state) => {
							state.state = "connected";
						});
						this.trigger();
						break;
					}
					default: {
						const _exhaustive: never = pbEvent;
					}
				}
				break;
			}
			case "system": {
				// Right now all system messages imply that the connection will be closed
				this.closedByServer = true;
				// Immediately stop sending pings so backend can destroy the connection
				clearInterval(this.pingTimer);

				const sysEvent = event.event;
				switch (sysEvent) {
					case "rejected": {
						this.state = produce(this.state, (state) => {
							state.state = "evicted";
						});
						this.trigger();
						break;
					}
					case "cancellation": {
						this.state = produce(this.state, (state) => {
							state.state = "cancelled";
						});
						this.trigger();
						break;
					}
					case "completion": {
						this.state = produce(this.state, (state) => {
							state.state = "completed";
						});
						this.trigger();
						break;
					}
					case "eviction": {
						this.state = produce(this.state, (state) => {
							state.state = "evicted";
						});
						this.trigger();
						break;
					}
					default: {
						const _exhaustive: never = sysEvent;
					}
				}
				break;
			}
			default: {
				const _exhaustive: never = event;
			}
		}
	}

	stop() {
		this.ws?.close();

		clearInterval(this.pingTimer);
		clearTimeout(this.reconnectTimer);

		this.ws = null;
		this.reconnectAttempt = 0;
		this.state = defaultBattleStoreState;
		this.trigger();
	}

	placeBid = (amount: number) => {
		this.ws?.send(
			JSON.stringify({
				type: "bid",
				amount,
			}),
		);
	};
}

export function useLiveBattle(
	id: string,
	{ connectIfNotLive = true }: { connectIfNotLive?: boolean } = {},
) {
	const { data: battle } = useSuspenseQuery(
		BBBackendQueryOptions.fetchBattle({ id }),
	);

	const store = useMemo(() => new BBBattleStore(id), [id]);

	useEffect(() => {
		if (!isLiveBattleStatus(battle.status) && !connectIfNotLive) {
			return;
		}

		store.connect();
		return () => store.stop();
	}, [store, battle.status, connectIfNotLive]);

	const state = useSyncExternalStore(
		store.subscribe,
		store.getSnapshot,
		() => defaultBattleStoreState,
	);

	return useMemo(() => {
		return {
			...state,
			...pick(store, ["placeBid"]),
		};
	}, [state, store]);
}

export function isLiveBattleStatus(status: BidBattle["status"]) {
	return status === "LIVE" || status === "PAUSED";
}
